import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import React from "react";
import axios from "axios";
import AdevalUpload from "app/adeval/adevalUpload";

interface Props {
  onResult: (result: any, ad: any) => void;
}

function apiUrl(path: string): string {
  const host = window.location.hostname;
  const port = window.location.port;
  const base = port ? `//${host}:${port}` : `//${host}`;
  return `${base}/copilot/genesys/adeval${path}`;
}

function parseCampaignId(input: string): string {
  // Accept full URL like https://creative.townsquaredigital.com/campaign/abc123
  // or just the ID directly
  try {
    const url = new URL(input);
    const parts = url.pathname.split("/").filter(Boolean);
    // Look for segment after "campaign"
    const idx = parts.indexOf("campaign");
    if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    // Fallback: last segment
    return parts[parts.length - 1] || input;
  } catch {
    return input.trim();
  }
}

const AdevalCreativeAi = ({ onResult }: Props) => {
  const [urlInput, setUrlInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [campaign, setCampaign] = React.useState<any>(null);

  const handleFetch = async () => {
    if (!urlInput.trim()) return;
    const campaignId = parseCampaignId(urlInput);
    setLoading(true);
    setError("");
    setCampaign(null);
    try {
      const res = await axios.get(apiUrl(`/creativeai/campaign/${encodeURIComponent(campaignId)}`));
      if (res.data.error) {
        setError(res.data.error);
      } else {
        setCampaign(res.data.campaign);
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || "Failed to fetch campaign");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Campaign URL input */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "flex-start" }}>
        <TextField
          label="CreativeAI Campaign URL or ID"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="https://creative.townsquaredigital.com/campaign/... or campaign ID"
          fullWidth
          size="small"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleFetch();
          }}
        />
        <Button variant="contained" onClick={handleFetch} disabled={loading || !urlInput.trim()}>
          Fetch
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Campaign info */}
      {campaign && (
        <Box>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              {/* Campaign image */}
              <Grid item xs={12} md={4}>
                {campaign.bgImageUrl && (
                  <Card>
                    <CardMedia
                      component="img"
                      image={campaign.bgImageUrl}
                      alt={campaign.campaignName}
                      sx={{ maxHeight: 250, objectFit: "contain" }}
                    />
                  </Card>
                )}
                {campaign.logoImageUrl && (
                  <Box sx={{ mt: 1, textAlign: "center" }}>
                    <img
                      src={campaign.logoImageUrl}
                      alt="Logo"
                      style={{ maxHeight: 40, objectFit: "contain" }}
                      onError={(e: any) => { e.target.style.display = "none"; }}
                    />
                  </Box>
                )}
              </Grid>

              {/* Campaign details */}
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  {campaign.campaignName}
                </Typography>
                {campaign.headline && (
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong>Headline:</strong> {campaign.headline}
                  </Typography>
                )}
                {campaign.secondary && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <strong>Secondary:</strong> {campaign.secondary}
                  </Typography>
                )}
                {campaign.ctatext && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>CTA:</strong> {campaign.ctatext}
                  </Typography>
                )}
                <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {campaign.vertical && (
                    <Chip label={campaign.vertical} size="small" variant="outlined" />
                  )}
                  {campaign.template && (
                    <Chip label={campaign.template} size="small" color="primary" variant="outlined" />
                  )}
                  {campaign.sizes && campaign.sizes.length > 0 && (
                    <Chip
                      label={`${campaign.sizes.length} size${campaign.sizes.length > 1 ? "s" : ""}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {campaign.color && (
                    <Chip
                      label={campaign.color}
                      size="small"
                      sx={{
                        bgcolor: campaign.color,
                        color: "#fff",
                        textShadow: "0 0 2px rgba(0,0,0,0.5)",
                      }}
                    />
                  )}
                </Box>
                {campaign.advertiserUrl && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {campaign.advertiserUrl}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          <Divider sx={{ mb: 3 }} />

          {/* Upload composed ad for this campaign */}
          <Typography variant="subtitle1" gutterBottom>
            Upload composed ad for scoring
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download the composed ad from CreativeAI, then upload or paste it here.
            Brand and vertical will be pre-filled from the campaign.
          </Typography>

          <AdevalUpload
            onResult={onResult}
            prefill={{
              brandName: campaign.campaignName || "",
              vertical: campaign.vertical || "",
              adSize: campaign.sizes?.[0] || "300x250",
            }}
          />
        </Box>
      )}

      {/* Show upload even without campaign */}
      {!campaign && !loading && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Or upload an ad directly without linking to a campaign:
          </Typography>
          <AdevalUpload onResult={onResult} />
        </Box>
      )}
    </Box>
  );
};

export default AdevalCreativeAi;
